import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { useState } from "react";
import { GoogleGroupIcon } from "../../../components/google-group/GoogleGroupIcon";
import { useCreateBorLetter } from "./data";
import styles from "./CreateBorModal.module.css";

export type ReportOption = {
  reportId: string;
  label: string;
  /** Google group email, shown so submissions for the same insured from
   *  different broker channels can be told apart. */
  googleGroup?: string;
};

type CreateBorModalProps = {
  visible: boolean;
  onHide: () => void;
  /** The report we're currently viewing */
  currentReportId: string;
  /** Available reports to pick the "other" report from */
  reportOptions: ReportOption[];
};

type Side = "cleared" | "incoming";

export const CreateBorModal: React.FC<CreateBorModalProps> = ({
  visible,
  onHide,
  currentReportId,
  reportOptions,
}) => {
  const [side, setSide] = useState<Side>("cleared");
  const [otherReportId, setOtherReportId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const createBor = useCreateBorLetter();

  const dropdownOptions = reportOptions
    .filter((r) => r.reportId !== currentReportId)
    .map((r) => ({
      label: r.label,
      value: r.reportId,
      googleGroup: r.googleGroup ?? "",
    }));

  type DropdownOption = (typeof dropdownOptions)[number];

  const optionTemplate = (option: DropdownOption) => (
    <div className={styles.option}>
      <span className={styles.optionInsured}>{option.label}</span>
      {option.googleGroup && (
        <span className={styles.optionGroup}>
          <GoogleGroupIcon groupName={option.googleGroup} />
        </span>
      )}
    </div>
  );

  const selectedTemplate = (option: DropdownOption | null) => {
    if (!option)
      return (
        <span className={styles.placeholder}>Select the other report...</span>
      );
    return (
      <div className={styles.selectedValue}>
        <span className={styles.optionInsured}>{option.label}</span>
        {option.googleGroup && (
          <span className={styles.optionGroup}>
            <GoogleGroupIcon groupName={option.googleGroup} />
          </span>
        )}
      </div>
    );
  };

  const handleSubmit = () => {
    if (!otherReportId) return;

    const clearedId = side === "cleared" ? currentReportId : otherReportId;
    const incomingId = side === "incoming" ? currentReportId : otherReportId;

    createBor.mutate(
      {
        cleared_report_id: clearedId,
        incoming_report_id: incomingId,
        notes: notes || null,
      },
      {
        onSuccess: () => {
          onHide();
          setSide("cleared");
          setOtherReportId(null);
          setNotes("");
        },
      }
    );
  };

  return (
    <Dialog
      header="Create BOR Letter"
      visible={visible}
      onHide={onHide}
      style={{ width: "480px" }}
      footer={
        <div className={styles.footer}>
          <Button label="Cancel" severity="secondary" text onClick={onHide} />
          <Button
            label="Create"
            onClick={handleSubmit}
            disabled={!otherReportId}
            loading={createBor.isPending}
          />
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.field}>
          <span className={styles.label}>This report is the...</span>
          <div className={styles.roleSelector}>
            <div
              className={`${styles.roleOption} ${side === "cleared" ? styles.roleOptionSelected : ""}`}
              onClick={() => setSide("cleared")}
            >
              Current broker
            </div>
            <div
              className={`${styles.roleOption} ${side === "incoming" ? styles.roleOptionSelected : ""}`}
              onClick={() => setSide("incoming")}
            >
              Blocked broker
            </div>
          </div>
        </div>

        <div className={styles.field}>
          <span className={styles.label}>
            {side === "cleared" ? "Blocked" : "Current"} broker&apos;s report
          </span>
          <Dropdown
            value={otherReportId}
            options={dropdownOptions}
            onChange={(e) => setOtherReportId(e.value)}
            placeholder="Select the other report..."
            filter
            filterBy="label,googleGroup"
            filterPlaceholder="Search by insured or group..."
            itemTemplate={optionTemplate}
            valueTemplate={selectedTemplate}
            showClear
            style={{ width: "100%" }}
            panelClassName={styles.dropdownPanel}
          />
        </div>

        <div className={styles.field}>
          <span className={styles.label}>Notes</span>
          <InputTextarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            rows={2}
          />
        </div>
      </div>
    </Dialog>
  );
};
